// SearchModal.tsx - site-wide search dropdown (mirrors trade). Uses the
// /search-site endpoint to surface Applications, Collections, Brands, Products
// (designs), Colours and Design Styles with direct navigation, plus product
// suggestions with images and a "See All Results" multifilter modal.

import { useEffect, useRef, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Button,
  Divider,
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import FilterModalSection from './FilterModalSection';
import SearchResultsModal from './SearchResultsModal';
import { apiFilterSearch } from '../../../api/apiSearch';
import { apiFilterProducts } from '../../../api/apiMultiFilter';

interface SelectedFilter {
  category: 'application' | 'range' | 'brand' | 'design' | 'colour' | 'design_style';
  value: string;
  displayCategory: string;
}

interface Product {
  id: string;
  sku: string;
  code: string;
  collection: string;
  colour: string;
  design: string;
  primaryImageUrl: string;
  is_published: boolean;
  application: string;
  item_group: string;
  range: string;
  web_colour: string;
  web_type: string;
  web_size: string;
  web_shape: string;
  web_materials: string;
  diameter: string;
  width: string;
  length_cm: string;
  design_style?: string;
}

interface PageDetail {
  application: string | null;
  brand: string | null;
  category_name: string;
  category_type: string;
  collection: string | null;
  id: string;
  item_group: string | null;
  item_group_name: string | null;
  new_arrival_active: boolean;
}

// The preview tries designs, then collections, one request each, until it has
// three products. Bound how many it will try so a broad search term can't turn
// into a request per match.
const MAX_PREVIEW_LOOKUPS = 5;

const getLowResImageUrl = (url: string): string => {
  if (!url) return '';
  return url.replace('/media/large/', '/media/small/');
};

function SearchModal({ modalIsOpen, handleCloseModal }: any) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  // Section visibility states
  const [showApplications, setShowApplications] = useState(false);
  const [showCollections, setShowCollections] = useState(false);
  const [showBrands, setShowBrands] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [showColours, setShowColours] = useState(false);
  const [showDesignStyles, setShowDesignStyles] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([]);
  const [searchResultsModalOpen, setSearchResultsModalOpen] = useState(false);
  const [isDirectNavigation, setIsDirectNavigation] = useState(false);

  const [previewProducts, setPreviewProducts] = useState<Product[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  const currentFilters = useMemo(() => ({
    application: selectedFilters.filter(f => f.category === 'application').map(f => f.value),
    range: selectedFilters.filter(f => f.category === 'range').map(f => f.value),
    brand: selectedFilters.filter(f => f.category === 'brand').map(f => f.value),
    design: selectedFilters.filter(f => f.category === 'design').map(f => f.value),
    colour: selectedFilters.filter(f => f.category === 'colour').map(f => f.value),
    design_style: selectedFilters.filter(f => f.category === 'design_style').map(f => f.value),
  }), [selectedFilters]);

  const {
    data: searchResultsData,
    isLoading,
    isError,
    isSuccess,
  } = useQuery(
    ['filter-search', searchTerm, currentFilters],
    () => apiFilterSearch(searchTerm, currentFilters),
    {
      enabled: !!searchTerm && searchTerm.length >= 2,
    }
  );

  // Extract filter arrays
  const filterApplications = useMemo(() => {
    if (!searchResultsData?.filters?.application) return [];
    return searchResultsData.filters.application;
  }, [searchResultsData]);

  // Extract designs from nested object structure
  const filterDesigns = useMemo(() => {
    if (!searchResultsData?.filters?.design) return [];

    const designs: string[] = [];

    searchResultsData.filters.design.forEach((designObj: any) => {
      const designName = Object.keys(designObj)[0];
      if (designName) {
        designs.push(designName);
      }
    });

    return designs;
  }, [searchResultsData]);

  // Extract ranges from design objects
  const rangesFromDesigns = useMemo(() => {
    if (!searchResultsData?.filters?.design) return [];

    const ranges: string[] = [];

    searchResultsData.filters.design.forEach((designObj: any) => {
      const designName = Object.keys(designObj)[0];
      if (designName && designObj[designName]?.range) {
        ranges.push(designObj[designName].range);
      }
    });

    return ranges;
  }, [searchResultsData]);

  // Merge collections from filters.range + ranges from design objects
  const filterCollections = useMemo(() => {
    const directRanges = searchResultsData?.filters?.range || [];

    // Merge and deduplicate
    const allRanges = [...directRanges, ...rangesFromDesigns];
    const uniqueRanges = Array.from(new Set(allRanges));

    return uniqueRanges;
  }, [searchResultsData, rangesFromDesigns]);

  const filterBrands = useMemo(() => {
    if (!searchResultsData?.filters?.brand) return [];
    return searchResultsData.filters.brand;
  }, [searchResultsData]);

  const filterColours = useMemo(() => {
    if (!searchResultsData?.filters?.colour) return [];
    return searchResultsData.filters.colour;
  }, [searchResultsData]);

  const filterDesignStyles = useMemo(() => {
    if (!searchResultsData?.filters?.design_style) return [];
    return searchResultsData.filters.design_style;
  }, [searchResultsData]);

  // Fetch product previews with images using design filter, range filter, or semantic search fallback
  useEffect(() => {
    // Set by the cleanup when the term (or its results) change, so a superseded
    // lookup stops issuing requests instead of walking its whole list.
    let cancelled = false;

    const fetchProductPreview = async () => {
      if (!searchTerm || searchTerm.length < 2) {
        setPreviewProducts([]);
        setPreviewLoading(false);
        return;
      }

      setPreviewLoading(true);

      try {
        const collectedProducts: Product[] = [];
        const maxProducts = 3;

        // Track products we've already collected so the same product never
        // appears twice (duplicate design names, or a product matching more
        // than one design/range would otherwise be pushed again).
        const seenKeys = new Set<string>();
        const productKey = (product: Product) => product.sku || product.id || product.code;

        const addProducts = (products: Product[] | undefined) => {
          if (!products) return;
          for (const product of products) {
            if (collectedProducts.length >= maxProducts) break;
            const key = productKey(product);
            if (key && seenKeys.has(key)) continue;
            if (key) seenKeys.add(key);
            collectedProducts.push(product);
          }
        };

        const previewFields = [
          'range', 'application', 'item_group', 'brand', 'web_colour',
          'design', 'design_style', 'web_materials', 'web_shape',
          'web_size', 'web_type', 'web_standards', 'web_composition', 'web_performance',
        ];

        // Priority 1: Try multiple designs until we have 3 unique products
        if (filterDesigns.length > 0) {
          for (const design of filterDesigns.slice(0, MAX_PREVIEW_LOOKUPS)) {
            if (cancelled || collectedProducts.length >= maxProducts) break;

            const response = await apiFilterProducts({
              filters: { design: [design] },
              deduplicate: true,
              filter_fields: previewFields,
              main_filter_field: null,
              selection_fields: null,
              sort_by_setting: 'Alphabetical A-Z',
              off_setter: { start: 0, end: maxProducts },
              only_published: true,
              only_promo: false,
              only_in_stock: false,
              minimum_stock: 1,
              promo_source: undefined,
              limited: true,
              user_query: undefined,
            });

            addProducts(response.products);
          }

          if (cancelled) return;
          if (collectedProducts.length > 0) {
            setPreviewProducts(collectedProducts);
            setPreviewLoading(false);
            return;
          }
        }

        // Priority 2: Try multiple collections/ranges until we have 3 unique products
        if (filterCollections.length > 0) {
          for (const range of filterCollections.slice(0, MAX_PREVIEW_LOOKUPS)) {
            if (cancelled || collectedProducts.length >= maxProducts) break;

            const response = await apiFilterProducts({
              filters: { range: [range] },
              deduplicate: true,
              filter_fields: previewFields,
              main_filter_field: null,
              selection_fields: null,
              sort_by_setting: 'Alphabetical A-Z',
              off_setter: { start: 0, end: maxProducts },
              only_published: true,
              only_promo: false,
              only_in_stock: false,
              minimum_stock: 1,
              promo_source: undefined,
              limited: true,
              user_query: undefined,
            });

            addProducts(response.products);
          }

          if (cancelled) return;
          if (collectedProducts.length > 0) {
            setPreviewProducts(collectedProducts);
            setPreviewLoading(false);
            return;
          }
        }

        // Priority 3: Fallback to semantic search if no design/range matches
        if (cancelled) return;
        const response = await apiFilterProducts({
          filters: {},
          deduplicate: true,
          filter_fields: previewFields,
          main_filter_field: null,
          selection_fields: null,
          sort_by_setting: 'Alphabetical A-Z',
          off_setter: { start: 0, end: maxProducts },
          only_published: true,
          only_promo: false,
          only_in_stock: false,
          minimum_stock: 1,
          promo_source: undefined,
          limited: true,
          user_query: searchTerm,
        });

        if (cancelled) return;
        addProducts(response.products);
        setPreviewProducts(collectedProducts);
      } catch (error) {
        if (cancelled) return;
        console.error('Product preview error:', error);
        setPreviewProducts([]);
      } finally {
        // A superseded run leaves the loading flag to the run that replaced it.
        if (!cancelled) setPreviewLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchProductPreview();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, filterDesigns, filterCollections]);

  const productSuggestions = previewProducts.slice(0, 3);

  const getCategoryDisplayName = (category: SelectedFilter['category']): string => {
    const displayNames = {
      application: 'Application',
      range: 'Collection',
      brand: 'Brand',
      design: 'Product',
      colour: 'Colour',
      design_style: 'Design Style',
    };
    return displayNames[category];
  };

  // Helper to get page detail (for item_group lookup from CMS pages)
  const getPageDetail = (categoryType: 'application' | 'collection' | 'brand', categoryName: string): PageDetail | null => {
    if (!searchResultsData?.collectionDetails) return null;

    const pageArrays = searchResultsData.collectionDetails;

    // Find in the appropriate array based on category type
    const pages = pageArrays[categoryType] || [];
    return pages.find((page: PageDetail) => page.category_name === categoryName) || null;
  };

  const handleAddFilter = (
    category: SelectedFilter['category'],
    value: string,
    displayCategory: string
  ) => {
    const alreadyExists = selectedFilters.some(
      f => f.category === category && f.value === value
    );

    if (!alreadyExists) {
      setSelectedFilters(prev => [...prev, { category, value, displayCategory }]);
    }
  };

  const handleRemoveFilter = (category: string, value: string) => {
    setSelectedFilters(prev => prev.filter(
      f => !(f.category === category && f.value === value)
    ));
  };

  // Handle navigation for all filter types
  const handleNavigateToFilter = (
    category: 'application' | 'range' | 'brand' | 'design' | 'colour' | 'design_style',
    value: string
  ) => {
    // ===== APPLICATIONS, COLLECTIONS, BRANDS - Direct Navigation =====
    if (category === 'application' || category === 'range' || category === 'brand') {
      const categoryTypeMap: Record<'application' | 'range' | 'brand', 'application' | 'collection' | 'brand'> = {
        application: 'application',
        range: 'collection',
        brand: 'brand',
      };

      // Try to get item_group from CMS pages first
      let pageDetail = getPageDetail(categoryTypeMap[category], value);
      let itemGroup = pageDetail?.item_group;

      // If no item_group from CMS pages and category is 'range', try getting it
      // from the rangeItemGroups map (extracted from design data)
      if (!itemGroup && category === 'range' && searchResultsData?.rangeItemGroups) {
        itemGroup = searchResultsData.rangeItemGroups[value];
      }

      if (itemGroup) {
        // Navigate based on category type
        if (category === 'application') {
          navigate(`/item_group/${itemGroup}/application/${encodeURIComponent(value)}`);
        } else if (category === 'brand') {
          navigate(`/item_group/${itemGroup}/brand/${encodeURIComponent(value)}`);
        } else if (category === 'range') {
          navigate(`/item_group/${itemGroup}/collection/${encodeURIComponent(value)}`);
        }
        handleCloseModal();
      } else {
        console.warn(`No item_group found for ${category}: ${value}`);
        // Fallback - add as filter and open multi-filter
        setSelectedFilters([{
          category,
          value,
          displayCategory: getCategoryDisplayName(category),
        }]);
        setIsDirectNavigation(true);
        setSearchResultsModalOpen(true);
        handleCloseModal();
      }
    }
    // ===== PRODUCTS (DESIGNS) - Direct Navigation =====
    else if (category === 'design') {
      // Direct navigation for designs using item_group from design data
      const designObj = searchResultsData?.filters?.design?.find((obj: any) => {
        const designName = Object.keys(obj)[0];
        return designName === value;
      });

      if (designObj) {
        const designData = designObj[value];
        const itemGroup = designData?.item_group;
        const range = designData?.range;

        if (itemGroup && range) {
          // Navigate to design page: /item_group/{item_group}/collection/{range}/design/{design}
          const encodedRange = encodeURIComponent(range);
          const encodedDesign = encodeURIComponent(value);

          navigate(`/item_group/${itemGroup}/collection/${encodedRange}/design/${encodedDesign}`);
          handleCloseModal();
          return;
        }
      }

      // Fallback to multi-filter if no item_group/range found
      console.warn(`No item_group/range found for design: ${value}`);
      setSelectedFilters([{
        category,
        value,
        displayCategory: getCategoryDisplayName(category),
      }]);
      setIsDirectNavigation(true);
      setSearchResultsModalOpen(true);
      handleCloseModal();
    }
    // ===== COLOURS, DESIGN STYLES - Multi-Filter Modal =====
    else {
      setSelectedFilters([{
        category,
        value,
        displayCategory: getCategoryDisplayName(category),
      }]);
      setIsDirectNavigation(true);
      setSearchResultsModalOpen(true);
      handleCloseModal();
    }
  };

  const handleProductClick = (product: Product) => {
    const itemGroup = product.item_group;
    const collection = product.range;
    const design = product.design;
    const colour = product.colour;
    const sku = product.sku;

    // Calculate size string (same logic as ProductGrid)
    const sizeString = product.diameter && product.diameter !== '0cm'
      ? product.diameter.trim()
      : `${product.width?.trim()}x${product.length_cm?.trim()}`;

    const encodedCollection = encodeURIComponent(collection);
    const encodedDesign = encodeURIComponent(design);
    const encodedColour = encodeURIComponent(colour);
    const encodedSize = encodeURIComponent(sizeString);
    const encodedSku = encodeURIComponent(sku);

    // Navigate to product page with targetProduct and targetSize params
    navigate(
      `/item_group/${itemGroup}/collection/${encodedCollection}/design/${encodedDesign}/colour/${encodedColour}?targetSize=${encodedSize}&targetProduct=${encodedSku}`
    );

    handleCloseModal();
  };

  const handleSearch = () => {
    if (!searchTerm.trim() && selectedFilters.length === 0) {
      return;
    }

    if (selectedFilters.length > 0) {
      setIsDirectNavigation(true);
    } else {
      setIsDirectNavigation(false);
    }

    setSearchResultsModalOpen(true);
    handleCloseModal();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (searchTerm.trim() || selectedFilters.length > 0)) {
      event.preventDefault();
      handleSearch();
    }
  };

  const handleCloseSearchResults = () => {
    setSearchResultsModalOpen(false);
    setSearchTerm('');
    setSelectedFilters([]);
    setIsDirectNavigation(false);
  };

  useEffect(() => {
    if (modalIsOpen) {
      setShowApplications(false);
      setShowCollections(false);
      setShowBrands(false);
      setShowProducts(false);
      setShowColours(false);
      setShowDesignStyles(false);
      setSearchTerm('');
      setSelectedFilters([]);
      setIsDirectNavigation(false);
      setPreviewProducts([]);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [modalIsOpen]);

  // Check if we have any results
  const hasAnyResults =
    filterApplications.length > 0 ||
    filterCollections.length > 0 ||
    filterBrands.length > 0 ||
    filterDesigns.length > 0 ||
    filterColours.length > 0 ||
    filterDesignStyles.length > 0 ||
    productSuggestions.length > 0;

  return (
    <>
      <Dialog
        onClose={handleCloseModal}
        open={modalIsOpen}
        fullWidth
        maxWidth="sm"
        onKeyDown={handleKeyDown}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            overflow: 'hidden',
          }}
        >
          <DialogTitle sx={{ flex: 1 }}>
            <Typography
              variant='caption'
              sx={{
                color: '#000',
                fontSize: { xs: '16px', lg: '24px' },
                fontWeight: '700',
                overflow: 'hidden',
              }}
            >
              Looking for something?
            </Typography>
          </DialogTitle>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              mr: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Close
              sx={{
                width: '20px',
                height: '20px',
              }}
            />
          </IconButton>
        </Box>
        <DialogContent sx={{ width: 'auto', pb: '50px' }}>

          <Box
            sx={{
              position: 'relative',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              padding: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'center',
              minHeight: '40px',
              flex: 1,
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&:focus-within': {
                borderColor: '#000',
                borderWidth: '2px',
                padding: '7px',
              },
            }}
            onClick={() => searchInputRef.current?.focus()}
          >
            {selectedFilters.map((filter, index) => (
              <Chip
                key={`${filter.category}-${filter.value}-${index}`}
                label={`${filter.displayCategory}: ${filter.value}`}
                onDelete={() => handleRemoveFilter(filter.category, filter.value)}
                size="small"
                sx={{
                  backgroundColor: '#e3f2fd',
                  border: '1px solid #2196f3',
                  maxWidth: '200px',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            ))}

            <TextField
              type='text'
              id='search'
              value={searchTerm}
              inputRef={searchInputRef}
              autoFocus
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              variant="standard"
              placeholder={selectedFilters.length === 0 ? "Search..." : ""}
              sx={{
                flex: 1,
                minWidth: '120px',
                '& .MuiInput-underline:before': {
                  borderBottom: 'none',
                },
                '& .MuiInput-underline:after': {
                  borderBottom: 'none',
                },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                  borderBottom: 'none',
                },
              }}
              InputProps={{
                disableUnderline: true,
              }}
            />
          </Box>

          {(isLoading || previewLoading) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {isError && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 2,
              }}
            >
              <Typography>
                An error occurred, please try again later
              </Typography>
            </Box>
          )}

          {isSuccess && searchTerm.length >= 2 && (
            <>
              {/* 1. Applications Section - DIRECT NAVIGATION */}
              {filterApplications.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Applications'}
                    items={filterApplications}
                    showSection={showApplications}
                    setShowSection={setShowApplications}
                    onItemClick={(value: string) => handleAddFilter('application', value, 'Application')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('application', value)}
                  />
                  {(filterCollections.length > 0 || filterBrands.length > 0 || filterDesigns.length > 0 || filterColours.length > 0 || filterDesignStyles.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 2. Collections Section - DIRECT NAVIGATION */}
              {filterCollections.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Collections'}
                    items={filterCollections}
                    showSection={showCollections}
                    setShowSection={setShowCollections}
                    onItemClick={(value: string) => handleAddFilter('range', value, 'Collection')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('range', value)}
                  />
                  {(filterBrands.length > 0 || filterDesigns.length > 0 || filterColours.length > 0 || filterDesignStyles.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 3. Brands Section - DIRECT NAVIGATION */}
              {filterBrands.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Brands'}
                    items={filterBrands}
                    showSection={showBrands}
                    setShowSection={setShowBrands}
                    onItemClick={(value: string) => handleAddFilter('brand', value, 'Brand')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('brand', value)}
                  />
                  {(filterDesigns.length > 0 || filterColours.length > 0 || filterDesignStyles.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 4. Products Section (design) - DIRECT NAVIGATION */}
              {filterDesigns.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Products'}
                    items={filterDesigns}
                    showSection={showProducts}
                    setShowSection={setShowProducts}
                    onItemClick={(value: string) => handleAddFilter('design', value, 'Product')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('design', value)}
                  />
                  {(filterColours.length > 0 || filterDesignStyles.length > 0) && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 5. Colours Section - MULTI-FILTER */}
              {filterColours.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Colours'}
                    items={filterColours}
                    showSection={showColours}
                    setShowSection={setShowColours}
                    onItemClick={(value: string) => handleAddFilter('colour', value, 'Colour')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('colour', value)}
                  />
                  {filterDesignStyles.length > 0 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 6. Design Styles Section - MULTI-FILTER */}
              {filterDesignStyles.length > 0 && (
                <>
                  <FilterModalSection
                    title={'Design Styles'}
                    items={filterDesignStyles}
                    showSection={showDesignStyles}
                    setShowSection={setShowDesignStyles}
                    onItemClick={(value: string) => handleAddFilter('design_style', value, 'Design Style')}
                    onItemNavigate={(value: string) => handleNavigateToFilter('design_style', value)}
                  />
                  {productSuggestions.length > 0 && (
                    <Divider sx={{ my: 2 }} />
                  )}
                </>
              )}

              {/* 7. Product Suggestions Section (at bottom) */}
              {productSuggestions.length > 0 && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography
                    variant='caption'
                    fontWeight={'600'}
                    sx={{
                      color: '#000',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                    }}
                  >
                    PRODUCT SUGGESTIONS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {productSuggestions.map((product: Product, index: number) => {
                      const imageUrl = getLowResImageUrl(product.primaryImageUrl);

                      return (
                        <Card
                          key={`${product.sku}-${index}`}
                          elevation={0}
                          sx={{
                            display: 'flex',
                            border: '1px solid #e0e0e0',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: 'action.hover',
                              borderColor: '#000',
                              transform: 'translateX(4px)',
                            },
                          }}
                          onClick={() => handleProductClick(product)}
                        >
                          <CardMedia
                            component="img"
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'contain',
                              p: 1,
                              backgroundColor: '#fafafa',
                            }}
                            image={imageUrl}
                            alt={product.design}
                          />
                          <CardContent sx={{ flex: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                textTransform: 'capitalize',
                                fontSize: '14px',
                                mb: 0.25,
                              }}
                            >
                              {product.design}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: '13px', mb: 0.25 }}
                            >
                              {product.colour}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: '12px' }}
                            >
                              {product.code}
                            </Typography>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Always show "See All Results" button when there's a search term */}
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Search />}
                onClick={handleSearch}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderColor: '#000',
                  color: '#000',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#000',
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                See All Results in Multi-Filter View
              </Button>

              {/* Show "No results found" message only if nothing found */}
              {!hasAnyResults && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant='caption'
                    sx={{
                      color: '#666',
                      fontSize: '14px',
                    }}
                  >
                    No results found. Click "See All Results" to search all products.
                  </Typography>
                </Box>
              )}
            </>
          )}

          {/* Show helpful message when less than 2 characters */}
          {searchTerm.length > 0 && searchTerm.length < 2 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant='caption' sx={{ color: '#666', fontSize: '14px' }}>
                Type at least 2 characters to search...
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-screen search results modal */}
      <SearchResultsModal
        open={searchResultsModalOpen}
        onClose={handleCloseSearchResults}
        searchQuery={isDirectNavigation ? '' : searchTerm.trim()}
        initialFilters={{
          ranges: selectedFilters.filter(f => f.category === 'range').map(f => f.value),
          designs: selectedFilters.filter(f => f.category === 'design').map(f => f.value),
          colours: selectedFilters.filter(f => f.category === 'colour').map(f => f.value),
          brands: selectedFilters.filter(f => f.category === 'brand').map(f => f.value),
          applications: selectedFilters.filter(f => f.category === 'application').map(f => f.value),
          design_styles: selectedFilters.filter(f => f.category === 'design_style').map(f => f.value),
        }}
      />
    </>
  );
}

export default SearchModal;
